import {Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {ProjectTimeFrame} from '../project-time-frame.type';
import {
  ProjectCharterControllerService,
  ProjectTimeFrameResponse,
  UpsertTimeFrameRequestParams,
} from '@frontend/shared-api';
import {ProjectDataService} from '../../../../../../../../shared/services/project-data.service';
import {Textarea} from 'primeng/textarea';
import {FormFieldComponent} from '../../../../../../../../shared/components/inputs/form-field/form-field.component';
import {ToastService} from '@frontend/toast';
import {ProjectStepSaveButtonComponent} from '../../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {FormValidationService} from '../../../../../../../../shared/services/form-validation.service';
import {FormFieldDatepickerComponent} from '../../../../../../../../shared/components/inputs/form-field/form-field-datepicker/form-field-datepicker.component';

@Component({
  selector: 'app-project-time-frame-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoPipe,
    Textarea,
    FormFieldComponent,
    FormsModule,
    ProjectStepSaveButtonComponent,
    FormFieldDatepickerComponent,
  ],
  templateUrl: './project-time-frame-form.component.html',
})
export class ProjectTimeFrameFormComponent {
  private readonly fb = inject(FormBuilder);
  private readonly projectCharterService = inject(ProjectCharterControllerService);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly toast = inject(ToastService);
  private readonly formValidationService = inject(FormValidationService);
  private readonly transloco = inject(TranslocoService);

  timeFrame = input.required<ProjectTimeFrame>();
  itemKey = input.required<string>();
  precedingTimeFrame = input<ProjectTimeFrame | undefined>(undefined);
  disabled = input<boolean>(false);
  savedTimeFrame = output<ProjectTimeFrameResponse>();

  readonly isSaving = signal<boolean>(false);
  readonly form = signal<FormGroup>(this.createForm());
  readonly formChanged = signal<boolean>(false);
  readonly formValues = signal<any>({});
  readonly hasChanges = computed(() => this.checkIfValuesChanged());
  readonly canSave = computed<boolean>(() => this.hasChanges() && !this.disabled());

  constructor() {
    effect(() => {
      this.updateForm();
    });

    // Add effect to handle disabled state
    effect(() => {
      const isDisabled = this.disabled();
      const stageDescriptionControl = this.form().get('stageDescription');

      if (isDisabled) {
        stageDescriptionControl?.disable({emitEvent: false});
      } else {
        stageDescriptionControl?.enable({emitEvent: false});
      }
    });

    this.form().valueChanges.subscribe((values) => {
      this.formChanged.set(true);
      this.formValues.set(values);
    });
  }

  onSave(): void {
    const form = this.form();
    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }

    const projectId = this.projectDataService.getProjectId();
    if (!projectId) {
      console.error('No project ID available');
      return;
    }

    this.isSaving.set(true);

    const requestParams = this.createUpsertRequest(form, projectId);
    this.projectCharterService.upsertTimeFrame(requestParams).subscribe({
      next: (response) => {
        this.isSaving.set(false);
        this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
        this.savedTimeFrame.emit(response);
      },
    });
  }

  private createUpsertRequest(form: FormGroup, projectId: number) {
    const formValue = form.value;
    const requestParams: UpsertTimeFrameRequestParams = {
      projectId,
      projectTimeFrameRequest: {
        id: this.timeFrame().id,
        milestone: formValue.milestone,
        startDate: formValue.startDate?.toISOString() ?? '',
        estimatedEndDate: formValue.estimatedEndDate?.toISOString() ?? '',
        stageDescription: formValue.stageDescription,
        includedProjectDescription: formValue.includedProjectDescription ?? true,
        directusId: this.timeFrame().directusId,
        tfNumber: this.timeFrame().tfNumber ?? 0,
      },
    };
    return requestParams;
  }

  private createForm(): FormGroup {
    return this.fb.group(
      {
        milestone: ['', Validators.required],
        startDate: [null, Validators.required],
        estimatedEndDate: [null, Validators.required],
        stageDescription: [''],
        includedProjectDescription: [false],
      },
      {
        validators: [
          this.formValidationService.dateRangeValidator('startDate', 'estimatedEndDate'),
          this.precedingEndDateValidator.bind(this),
        ],
      },
    );
  }

  private precedingEndDateValidator(group: FormGroup): {[key: string]: any} | null {
    const precedingTF = this.precedingTimeFrame();
    if (!precedingTF?.estimatedEndDate) return null;

    const estimatedEndDate = group.get('estimatedEndDate')?.value;
    if (!estimatedEndDate) return null;

    const precedingEndDate = new Date(precedingTF.estimatedEndDate);
    const currentEndDate = new Date(estimatedEndDate);

    if (currentEndDate < precedingEndDate) {
      group.get('estimatedEndDate')?.setErrors({precedingEndDate: true});
      return {precedingEndDate: true};
    }

    return null;
  }

  isEndDateBeforePrecedingEndDate(): boolean {
    return this.form().hasError('precedingEndDate');
  }

  isEndDateBeforeStartDate(): boolean {
    return this.form().hasError('dateRange');
  }

  private updateForm(): void {
    const timeFrameData = this.timeFrame();
    const formData = {
      milestone:
        timeFrameData.milestone ||
        timeFrameData?.translations
          ?.filter((trans) => trans.language === this.transloco.getActiveLang())[0]
          ?.name?.replace('*', ''),
      startDate: timeFrameData.startDate ? new Date(timeFrameData.startDate) : null,
      estimatedEndDate: timeFrameData.estimatedEndDate
        ? new Date(timeFrameData.estimatedEndDate)
        : null,
      stageDescription: timeFrameData.stageDescription || '',
      includedProjectDescription: timeFrameData.includedProjectDescription ?? null,
    };

    this.form().patchValue(formData, {emitEvent: false}); // Don't trigger valueChanges
    this.formValues.set(formData);

    this.formChanged.set(false);
  }

  private checkIfValuesChanged(): boolean {
    const timeFrame = this.timeFrame();
    const formValue = this.formValues();
    const hasFormChanged = this.formChanged();

    if (this.timeFrame().suggested) {
      return true;
    }

    if (!timeFrame.id) return hasFormChanged;

    const fieldsToCompare = [
      {
        formValue: formValue.milestone?.trim() || '',
        savedValue: timeFrame.milestone || '',
      },
      {
        formValue: formValue.startDate?.toISOString() || '',
        savedValue: timeFrame.startDate ? new Date(timeFrame.startDate).toISOString() : '',
      },
      {
        formValue: formValue.estimatedEndDate?.toISOString() || '',
        savedValue: timeFrame.estimatedEndDate
          ? new Date(timeFrame.estimatedEndDate).toISOString()
          : '',
      },
      {
        formValue: formValue.stageDescription?.trim() || '',
        savedValue: timeFrame.stageDescription || '',
      },
      {
        formValue: formValue.includedProjectDescription || false,
        savedValue: timeFrame.includedProjectDescription || false,
      },
    ];

    return fieldsToCompare.some(({formValue, savedValue}) => formValue !== savedValue);
  }
}
